import React, { useState } from "react";
import HostDestinationCard from "../../hospitality/HostDestinationCard";
import { ShoppingBag } from "lucide-react";
import { resolveTenantLogoUrl } from "../../../lib/retailFormat";
import StorefrontModal from "./StorefrontModal";
import PastOrdersLookup from "./PastOrdersLookup";
import { storefrontRequiresLoyaltyOtp } from "../../../lib/loyaltyOtpPolicy";
import LoyaltyPointsPanel from "./LoyaltyPointsPanel";
import VerificationModal from "./VerificationModal";
import { digitsOnly, formatUsPhoneDisplay, isValidEmail, isValidPhone, normalizeEmail, toE164 } from "../../../lib/contactValidation";
import { addressFingerprint, suggestionDiffersFromSubmitted, suggestionToWelcomeShape, welcomeAddressToValidateBody } from "../../../lib/shipToAddressValidation";
import { logCustomerAuditEvent } from "../../../lib/customerAuditLog";

// Phase 3M — extracted verbatim from RetailCustomer.jsx. Behaviour identical
// (mode filtering, conditional contact + address fields, inline error copy).

// Phase 3L.5 — base copy is restaurant-flavoured (the original/beta
// vertical). When the catalog response carries `order_mode_labels`
// (vertical-aware), we substitute those at render time instead of
// hard-coding restaurant wording for every storefront. Icons + helper
// descriptions stay generic.
// Level 1 — Tenant Membership Foundation (Aug 2026). Version tag for the
// disclosure text rendered in the membership checkbox label below —
// bump this whenever that copy changes so `consent_captured_at`/
// `consent_version` on the stored membership row stay a durable,
// dated record of exactly which text a customer accepted.
const MEMBERSHIP_CONSENT_TEXT_VERSION = "v1.0";

const ORDER_MODE_FALLBACKS = {
  dine_in: { icon: "🍽", desc: "On-premise" },
  takeout: { icon: "🥡", desc: "Take with you" },
  pickup:  { icon: "🛍", desc: "Order now, pick up later" },
  delivery:{ icon: "🛵", desc: "We'll deliver to your address" },
  // CP2.2 — Ship to My Address (parcel shipping). DISTINCT from
  // `delivery` which remains local courier / DoorDash / Uber /
  // tenant driver. Ship-to-Address routes through FedEx / USPS.
  shipping:{ icon: "📦", desc: "Ship via FedEx / USPS to my address" },
};

// Phase 3A.5 Final Checkout Integration correction — Local Delivery
// address-validation state machine, mirroring the `shippingValidation`
// pattern above it but calling the Local-Delivery-only routes. A stable
// module-level constant (not recreated per render) so it can be used
// both as the initial state and as the reset value on any edit/mode
// change/remount.
const LOCAL_DELIVERY_VALIDATION_IDLE = {
  status: "idle", // idle | checking | needs_confirmation | invalid | confirming | confirmed | error
  validationId: null,
  checkoutAttemptToken: null,
  normalizedAddress: null,
  latitude: null,
  longitude: null,
  serverFingerprint: null,
  serviceabilityStatus: null,
  reasonCode: null,
  message: "",
};

// Pure function — no component state — so it's directly unit-testable.
// The backend's validate/confirm responses carry a `reason_code`, not a
// customer-facing message (Phase 3A.5 §12: never persist/return raw
// provider text); this is the one place that maps the documented reason
// codes onto copy a customer can act on.
export function localDeliveryReasonMessage(reasonCode) {
  if (!reasonCode) return "We couldn't confirm this address is deliverable. Please check it and try again.";
  if (reasonCode === "po_box_not_supported") return "We can't deliver to PO boxes. Please enter a street address.";
  if (reasonCode.startsWith("serviceability_blocked")) return "This address is outside our delivery area.";
  if (reasonCode.startsWith("serviceability_unknown")) return "We can't confirm delivery to this address right now. Please try again shortly.";
  if (reasonCode === "missing_street_number") return "Please include a street number in your address.";
  if (reasonCode === "address_needs_correction") return "This address needs correction. Please double-check it.";
  if (reasonCode === "coordinates_unavailable") return "We couldn't locate this address. Please double-check it.";
  if (reasonCode.startsWith("precision_too_coarse")) return "Please enter a more specific street address.";
  return "We couldn't confirm this address is deliverable. Please check it and try again.";
}

function Field({ label, optional, children }) {
  return (
    <label className="block mb-4">
      <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 flex items-center gap-2">
        <span>{label}</span>
        {optional && <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-stone-100 text-stone-500">Optional</span>}
      </div>
      {children}
    </label>
  );
}

export default function WelcomeModal({
  tenant, slug, catalog, sessionId, initial, orderModes, enabledModeList, labelFor, onCancel, onSubmit,
  recognize, onDraftChange, onRecognized, validateShippingAddress,
  // Phase 3A.5 Final Checkout Integration correction.
  localDeliveryAddressValidationEnabled = false, validateLocalDeliveryAddress, confirmLocalDeliveryAddress,
  // Level 1 — Tenant Membership Foundation (Aug 2026).
  enrollMembership,
  // Customer Favorites (Aug 2026) — lifts the just-completed OTP proof
  // to the parent (RetailCustomer.jsx), mirroring `onRecognized`
  // exactly, so Favorites on the menu screen can reuse it within its
  // own freshness window without a second OTP prompt. Optional: a
  // caller that doesn't pass this simply doesn't get Favorites
  // enabled on the menu screen, nothing else changes.
  onVerifiedProof,
  // Loyalty-only proof lift — see the PastOrdersLookup wiring below.
  onLoyaltyProof,
  // Walk-in customer recognition/verification correction (Aug 2026) —
  // a SAFE, pre-OTP lookup (never sends/requires an OTP, never exposes
  // name/email/address/order history) used ONLY to pre-check + disable
  // the SMS-consent/Membership checkboxes for a genuinely returning
  // customer/member. Optional: a caller that doesn't pass this simply
  // never auto-locks either checkbox, nothing else changes.
  checkSafeStatus,
  // Final Operator-Validation Prerequisites correction (Aug 2026,
  // Product Owner final OTP decision) — lifts the SAFE (non-OTP)
  // recognition result to the parent so it can grant no-OTP Favorites
  // access (mirrors `onVerifiedProof`'s lift, but for the safe path).
  // Called with `{ active, phone, email }` once a walk-in-mode safe
  // check settles, or `null` when the identity is cleared/edited.
  // Optional: a caller that doesn't pass this simply never gets no-OTP
  // Favorites access — the rest of the safe pre-check (checkbox
  // pre-check) is unaffected either way.
  onSafeRecognized,
  // Final Operator-Validation Prerequisites correction (Aug 2026) — the
  // no-OTP counterpart to `enrollMembership`, called with the SAME
  // consent fields but a `phone` instead of an OTP proof. Optional: a
  // caller that doesn't pass this simply never enrolls a walk-in
  // customer without OTP (the checkbox still records intent, but
  // nothing is submitted for it in walk-in mode without this prop).
  enrollMembershipSafe,
  // Hospitality H1 (Sep 2026) — set only for a hotel-referred session.
  // The order is Local Delivery to the hotel's stored address, which the
  // SERVER locks at checkout; here the mode picker and address form are
  // replaced by a read-only destination card. `null` → unchanged modal.
  hostDestination = null,
}) {
  const hostLocked = !!hostDestination;
  // Final Operator-Validation Prerequisites correction (Aug 2026,
  // Product Owner final OTP decision) — hoisted above every effect
  // below that needs it. `isWalkInMode` scopes EVERY no-OTP behavior
  // in this component (safe-first recognition messaging, no-OTP
  // Favorites/membership) to EXACTLY Dine-In and Takeout — the PO's
  // explicit instruction not to touch Local Delivery, Online Orders,
  // or Shipping means those order types keep the pre-existing,
  // unchanged OTP-gated behavior below, unconditionally.
  const enabledList = enabledModeList && enabledModeList.length > 0
    ? enabledModeList
    : ["dine_in", "takeout"];
  const [orderType, setOrderType] = useState(
    hostLocked ? "delivery" : (enabledList.includes(initial?.orderType) ? initial.orderType : enabledList[0])
  );
  const isWalkInMode = orderType === "dine_in" || orderType === "takeout";
  // Hospitality H1 — if the hotel context arrives AFTER this modal opened
  // (e.g. the guest already tapped Pickup), converge to Delivery at once:
  // a hotel-referred order can only be Local Delivery to the hotel.
  React.useEffect(() => {
    if (hostLocked && orderType !== "delivery") setOrderType("delivery");
  }, [hostLocked, orderType]);

  // Tenant logo (Sep 2026 restoration) — `tenant.logo_url` is stored as
  // a BACKEND-relative path, so it is resolved against the API origin
  // exactly once, here, and reset whenever the tenant changes. A stored
  // file that no longer exists falls back to the same placeholder an
  // unset logo gets, so a dead reference never leaves a broken-image
  // glyph on the screen a customer sees first.
  const [logoBroken, setLogoBroken] = useState(false);
  React.useEffect(() => { setLogoBroken(false); }, [tenant?.logo_url]);
  const logoSrc = logoBroken ? "" : resolveTenantLogoUrl(tenant?.logo_url || "");

  const [name, setName] = useState(initial?.name || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  // Initial go-live completion pass (Aug 2026) — returning-customer
  // past-order history entry point. See `PastOrdersLookup.jsx`.
  const [showPastOrders, setShowPastOrders] = useState(false);
  // Sep 2026 — whichever "View my past orders" trigger opened the dialog
  // is remembered here and re-focused when it closes, so a keyboard or
  // screen-reader user lands back where they were instead of at the top
  // of the form. Radix restores focus on unmount on its own; this makes
  // the behaviour explicit and independently assertable, and covers the
  // nested-dialog case where the inner dialog closes while the outer
  // welcome dialog stays open.
  const pastOrdersTriggerRef = React.useRef(null);
  // Loyalty Phase 1 (Sep 2026) — the customer's own points view. Gated on
  // the SAME OTP-verified proof Past Orders uses: points are money, so a
  // typed phone number must never reveal a balance.
  const [showLoyaltyPoints, setShowLoyaltyPoints] = useState(false);
  // Post-deployment security correction (Aug 2026) — controls the OTP
  // step below. See the removed auto-debounce comment (further down)
  // for what this replaced and why.
  const [showRecognizeVerify, setShowRecognizeVerify] = useState(false);
  // Phase 2A — returning-customer recognition via the public
  // /recognize endpoint. If a known, OTP-VERIFIED customer + active
  // coupon is returned, a banner is shown above the form. The coupon
  // is auto-applied at checkout — the customer is never asked to type
  // the code.
  //
  // Post-deployment security correction (Aug 2026) — `/recognize`
  // previously ran automatically, unauthenticated, the instant a
  // customer typed 10 phone digits, and returned their name, email,
  // full saved addresses, last-order date, and order count with no
  // verification at all (confirmed in a live staging validation
  // pass). `recognize()` now requires an already-completed OTP proof
  // (`routes/commerce.py`'s module docstring) — this component no
  // longer calls it silently on every keystroke; it only calls it
  // (via `runRecognize` below) AFTER `VerificationModal` reports a
  // successful verification, passing that proof along. Before that,
  // the phone/email fields show only a neutral "verify to retrieve
  // saved details" prompt (see the JSX below), never prefilled data.
  //
  // PR #41 hardening (Phase 2 — validated address as session context) —
  // seeded from `initial.recognized` when the parent already has it
  // (e.g. this modal remounted after Back+Start again this session), so
  // the Welcome-back banner + saved-address picker render immediately
  // instead of waiting on a fresh network round trip. The recognition
  // result itself was previously trapped in this component's local
  // state and lost on every unmount.
  const [recognized, setRecognized] = useState(initial?.recognized || null);
  // UX correction (Aug 2026) — the OTP proof that unlocked `recognized`,
  // kept so "View my past orders" can reuse it (§C below) instead of
  // asking for a second phone number and a second OTP. Cleared the
  // instant identity changes (see the invalidation effect further
  // down) so a stale proof can never outlive the phone/email it was
  // verified against.
  const [verifiedProof, setVerifiedProof] = useState(null);
  // Feb 2026 — track whether the user has manually touched the SMS
  // consent checkbox. If they have, we NEVER auto-flip it based on
  // recognition — respects their explicit intent. If they haven't and
  // the returning customer previously agreed to SMS, we default the
  // checkbox to checked so repeat customers aren't asked twice.
  const [smsConsentTouched, setSmsConsentTouched] = useState(false);
  // Iter55 — A2P 10DLC informational/transactional SMS consent.
  // Unchecked by default per CTIA guidelines. Restored if customer
  // already agreed earlier this session. Declared here (rather than
  // further down, where it originally lived) because the recognize
  // auto-trigger effect below now reads it — a `const` referenced
  // before its own declaration in the same component body throws at
  // render time (temporal dead zone), it doesn't silently see `undefined`.
  const [smsConsent, setSmsConsent] = useState(!!initial?.smsConsent);
  // Vercel production-build fix (Aug 2026, PR #121 follow-up) —
  // `react-hooks/exhaustive-deps` correctly flags the recognize auto-
  // trigger effect below for reading `smsConsent` without depending on
  // it (CI treats that warning as a build error). The dependency
  // itself cannot simply be added — that would re-run the effect (and
  // re-arm its 700ms auto-fire) the instant the checkbox changes,
  // reintroducing "check the box, get a code with no further click"
  // for a phone number that already settled. This ref mirrors
  // `smsConsent` on every render; the effect reads `smsConsentRef.
  // current` instead of the raw state variable, which the rule does
  // not require in a dependency array (ref reads are stable), so the
  // gate is enforced honestly with no eslint-disable and no
  // suppression — only the effect's OWN triggering conditions
  // (phone/email/recognize/recognizableChannel/recognized settling)
  // still decide when it re-evaluates.
  const smsConsentRef = React.useRef(smsConsent);
  smsConsentRef.current = smsConsent;
  const smsConsentTouchedRef = React.useRef(smsConsentTouched);
  smsConsentTouchedRef.current = smsConsentTouched;
  // Walk-in customer recognition/verification correction (Aug 2026) —
  // whether the SAFE (non-OTP) lookup found active SMS consent /
  // active membership for the currently-typed phone/email. Purely a
  // DISPLAY/lock flag: true forces the corresponding checkbox to
  // render checked+disabled ("this customer already agreed" /
  // "already a member" — never ask again), false leaves it exactly as
  // freely editable as it always was. Never itself the security
  // enforcement — the backend independently re-derives both facts on
  // every OTP verification/enrollment call, exactly as it already did
  // before this correction.
  const [safeSmsConsentActive, setSafeSmsConsentActive] = useState(false);
  const [safeMembershipActive, setSafeMembershipActive] = useState(false);
  // Monotonic per-lookup generation counter — "Prevent slow recognition
  // responses for an old number from overwriting a newer phone-number
  // entry" (required behavior). Incremented synchronously the instant a
  // new lookup is scheduled; a response is applied only if it's still
  // the LATEST one scheduled by the time it resolves.
  const safeStatusGenerationRef = React.useRef(0);
  // Level 1 — Tenant Membership Foundation (Aug 2026). Deliberately a
  // SEPARATE piece of state from `smsConsent` — never read, set, or
  // touched together, so membership consent can never be inferred from
  // (or confused with) SMS consent. Unchecked by default; only shown
  // when this tenant has Membership enabled (`tenant.membership_config
  // .enabled`, the public flag `routes/retail_public.py` exposes).
  const [membershipConsent, setMembershipConsent] = useState(false);
  // Result of a just-completed enroll call this session — distinct
  // from `recognized.member`, which reflects an ALREADY-existing
  // membership from a prior visit. Kept separate so the UI can show
  // "Membership activated" only after the backend itself confirms it,
  // never optimistically from the checkbox alone.
  const [membershipJustEnrolled, setMembershipJustEnrolled] = useState(null);
  const [membershipEnrollError, setMembershipEnrollError] = useState(null);
  const membershipEnabled = !!tenant?.membership_config?.enabled;
  // Walk-in customer recognition/verification correction (Aug 2026) —
  // an already-active membership (from the SAFE, pre-OTP lookup above)
  // always shows checked+disabled, unconditionally — never gated by
  // whether the customer has "touched" it, since there is no fresh
  // enrollment intent to protect here (submitting later is a safe
  // no-op: `handleRecognizeVerified` below already skips `enrollMembership`
  // whenever the just-verified proof's own `member` is already truthy).
  React.useEffect(() => {
    if (safeMembershipActive) setMembershipConsent(true);
  }, [safeMembershipActive]);
  // Live-acceptance correction (Aug 2026) — Membership's identity
  // foundation is a verified mobile number, so joining requires
  // transactional SMS consent as a UI/enrollment PREREQUISITE. This is
  // deliberately a one-way dependency (SMS consent gates Membership,
  // never the reverse) and stays a prerequisite check only — it never
  // reads, writes, or derives `smsConsent` FROM `membershipConsent`,
  // and the backend independently re-verifies the same requirement
  // (see `routes/commerce.py::enroll_membership`), so this effect is a
  // UX convenience, never the actual enforcement boundary. If the
  // customer unchecks SMS consent after having selected Membership,
  // the stale membership intent must not silently survive — clearing
  // it here (rather than merely disabling the checkbox going forward)
  // guarantees a re-render with `membershipConsent` false, so no
  // stale closure/click-race can still submit an enrollment.
  React.useEffect(() => {
    if (!smsConsent && membershipConsent) {
      setMembershipConsent(false);
    }
  }, [smsConsent, membershipConsent]);
  // Go-Live Enhancement (Feb 2026, Dine-In / Takeout optional contact
  // capture) — recognise a returning customer from EITHER phone OR
  // email, whichever channel the OTP proof was verified against.
  const runRecognize = React.useCallback(async (arg) => {
    if (!recognize) return null;
    try {
      const r = await recognize(arg);
      if (!r || !r.customer) return r || null;
      setRecognized(r);
      onRecognized?.(r);
      if (r.customer.name) setName((prev) => prev || r.customer.name);
      if (r.customer.email) setEmail((prev) => prev || r.customer.email);
      if (r.customer.phone) setPhone((prev) => prev || r.customer.phone);
      if (r.customer.sms_consent_agreed && !smsConsentTouched) {
        setSmsConsent(true);
      }
      const addrs = r.customer.addresses || [];
      if (addrs.length > 0) {
        setDeliveryAddress((prev) => {
          if (prev?.line1 || prev?.city) return prev;
          const pick = addrs.find((a) => a.is_default) || addrs[0];
          return {
            address_id: pick.address_id,
            line1: pick.street || "", line2: pick.line2 || "",
            city: pick.city || "", state: pick.state || "",
            postal_code: pick.postal_code || "", country: pick.country || "US",
            instructions: pick.instructions || "",
          };
        });
        setAddressMode("saved");
      }
      return r;
    } catch (_e) {
      // Ignore transient failures — the customer can still complete
      // the order without recognition.
      return null;
    }
  }, [recognize, smsConsentTouched, onRecognized]);

  const [email, setEmail] = useState(initial?.email || "");
  // Post-deployment security correction (Aug 2026) — which channel a
  // "Verify to see your saved details" action should verify: phone
  // once it's a complete 10-digit number (matches the pre-fix debounce
  // priority), else a validly-formatted email, else neither (no prompt
  // shown at all). Purely derived — issues no network call by itself.
  // Phone/OTP validation correction (Aug 2026) — confirmed staging
  // defect: `digitsOnly(phone).length >= 10` accepted any 10-OR-MORE
  // digit string (e.g. the 11-digit "408-823-10402", which the field's
  // own `isValidPhone` hint below correctly flags as incomplete/invalid)
  // as "recognizable," auto-opening the OTP modal for a number this
  // codebase itself had just told the customer was invalid. Uses the
  // SAME strict `isValidPhone` this file already imports and already
  // uses for the visible error hint, so the two can never disagree.
  const recognizableChannel = isValidPhone(phone)
    ? "sms"
    : (isValidEmail((email || "").trim()) ? "email" : null);

  // Walk-in customer recognition/verification correction (Aug 2026) —
  // the SAFE (never-OTP) pre-check effect. Deliberately its OWN effect,
  // entirely independent of the OTP auto-trigger effect just below —
  // this one never opens `VerificationModal` and never sends anything;
  // it only asks the two non-PII yes/no questions
  // (`checkSafeStatus`/`services/customers.py::get_safe_recognition_signals`)
  // needed to pre-check + disable the SMS-consent/Membership checkboxes.
  React.useEffect(() => {
    if (!checkSafeStatus) return;
    // Any phone/email edit immediately clears the prior lock — "restore
    // the appropriate editable defaults" — before a possibly-different
    // fresh result comes back. A single retyped digit is still a
    // material edit: we never keep showing a PRIOR number's active-
    // consent/membership status once its exact value has changed.
    setSafeSmsConsentActive(false);
    setSafeMembershipActive(false);
    // Final Operator-Validation Prerequisites correction (Aug 2026) —
    // a cleared/edited identity also clears the lifted safe-recognition
    // grant (Favorites access) the SAME instant it clears the local
    // checkbox locks, for the SAME reason — a stale grant must never
    // outlive the exact phone/email it was derived from.
    if (isWalkInMode) onSafeRecognized?.(null);
    if (!recognizableChannel) return;
    const destinationValue = recognizableChannel === "sms" ? phone : (email || "").trim();
    const myGeneration = ++safeStatusGenerationRef.current;
    const t = setTimeout(async () => {
      try {
        const body = recognizableChannel === "sms"
          ? { phone: destinationValue }
          : { email: destinationValue };
        const result = await checkSafeStatus(body);
        if (myGeneration !== safeStatusGenerationRef.current) return; // superseded by a newer entry
        setSafeSmsConsentActive(!!result?.has_active_sms_consent);
        setSafeMembershipActive(!!result?.has_active_membership);
        if (result?.has_active_sms_consent && !smsConsentTouchedRef.current) {
          setSmsConsent(true);
        }
        // Correct the Failed PR #140/#142 Walk-In Workflow (Sep 2026)
        // — confirmed staging defect: the header later showed "Hello,
        // <name>" (from a separate, later OTP-verified `/recognize`
        // call) but the Sales Order form's own "Your Name" field
        // stayed blank the entire time, even though this SAME safe
        // lookup already resolved the customer. `services/customers.
        // py::get_safe_recognition_signals` now includes `name`
        // whenever it includes either active signal (see its own
        // docstring for the non-enumeration reasoning); mirrors
        // `runRecognize`'s own `setName((prev) => prev || ...)`
        // pattern exactly — never overwrites a name the customer
        // already typed themselves.
        if (result?.name) {
          setName((prev) => prev || result.name);
        }
        // Final Operator-Validation Prerequisites correction (Aug
        // 2026, Product Owner final OTP decision) — Favorites
        // access/changes for a recognized active member are an
        // explicitly accepted, non-OTP walk-in convenience, scoped to
        // EXACTLY Dine-In/Takeout (never Delivery/Shipping/Online
        // Order — this is the ONE new grant this correction adds, and
        // it is deliberately as narrow as the SAFE lookup itself: no
        // PII, no order history, membership status only).
        if (isWalkInMode) {
          onSafeRecognized?.({
            active: !!result?.has_active_membership,
            phone: recognizableChannel === "sms" ? destinationValue : null,
            email: recognizableChannel === "email" ? destinationValue : null,
          });
        }
      } catch (_e) {
        // Network/unexpected failure — never blocks the customer; both
        // checkboxes simply stay freely editable (fail OPEN to
        // "editable," never to "silently checked"), and no Favorites
        // grant is lifted either.
      }
    }, 700);
    return () => clearTimeout(t);
    // `onSafeRecognized` deliberately excluded — it is an inline
    // callback on the parent (RetailCustomer.jsx) whose identity
    // changes every parent render; including it here (confirmed via a
    // "Maximum update depth exceeded" failure while writing this
    // correction's own tests: `favorites.reset()` on the parent always
    // creates a fresh empty Set, a new reference even when logically
    // unchanged, which re-renders the parent, creates a new callback
    // identity, and re-fires this effect) would loop indefinitely.
    // This effect's own triggering conditions (phone/email/
    // recognizableChannel/checkSafeStatus/isWalkInMode) are exactly
    // what should re-evaluate it — mirrors the SAME exclusion pattern
    // already established for `onRecognized` on the auto-trigger
    // effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, email, recognizableChannel, checkSafeStatus, isWalkInMode]);

  // UX correction (Aug 2026) — automatic single-OTP initiation. Once a
  // complete phone number (or, failing that, a valid email) settles
  // for 700ms, the verification step opens BY ITSELF — no "Verify"
  // click required. `autoTriggeredForRef` remembers the exact channel
  // + value this already fired for, so re-renders, focus/blur, and the
  // debounce effect re-running on every keystroke can only ever open
  // it once per distinct value; a genuinely NEW number (a different
  // key) is free to trigger again. If the customer closes the modal
  // without completing, this deliberately does NOT reopen for the SAME
  // value — the manual prompt+button below remains as the "clear retry
  // control" for that case, never a second automatic send.
  const autoTriggeredForRef = React.useRef(null);
  React.useEffect(() => {
    // Final Operator-Validation Prerequisites correction (Aug 2026,
    // Product Owner final OTP decision) — this entire auto-OTP-send
    // effect is now scoped OUT of Dine-In/Takeout entirely. Rudras OTP
    // for walk-in customers is required ONLY for "View my past
    // orders" (that flow's own independent verification lives in
    // `PastOrdersLookup.jsx` and is untouched by this correction).
    // Delivery/Shipping/Online Order are explicitly out of this
    // correction's scope and keep this exact pre-existing behavior.
    if (isWalkInMode) return;
    if (!recognize) return;
    if (recognized?.customer) return; // already verified this session
    if (!recognizableChannel) return;
    // New-customer SMS consent fix (Aug 2026) — confirmed staging
    // defect: this auto-trigger fired (and `VerificationModal`
    // dispatched a real OTP) for the SMS channel the instant a
    // complete phone number settled, even with the SMS-consent
    // checkbox below still unchecked. That checkbox's own copy ("I
    // agree to receive order updates and verification codes via SMS")
    // explicitly covers verification codes, so SMS auto-issuance now
    // requires that consent to ALREADY be true at the moment the
    // phone number settles — the returning-customer case, where
    // consent is pre-checked from an earlier visit this session
    // (`initial?.smsConsent`) or defaulted from recognition. Reads
    // `smsConsentRef.current` (see its declaration above) rather than
    // the raw `smsConsent` state on purpose: mandatory-consent-audit
    // correction (Aug 2026, PR #121 follow-up) — confirmed requirement
    // that checking the box is never itself a trigger, only an
    // explicit "Send verification code" action is (the manual button
    // below, or `VerificationModal`'s own inline recovery). Depending
    // on `smsConsent` directly here would re-run this effect (and
    // re-arm its 700ms auto-fire) the instant the checkbox changes,
    // reintroducing "checked the box, got a code with no further
    // click" for a phone number that already settled — the ref keeps
    // this effect's own triggering conditions
    // (phone/email/recognize/recognizableChannel/recognized) as the
    // only things that re-evaluate it, while still reading the LATEST
    // consent value at that moment.
    if (recognizableChannel === "sms" && !smsConsentRef.current) return;
    const destinationValue = recognizableChannel === "sms" ? phone : (email || "").trim();
    const key = `${recognizableChannel}:${destinationValue}`;
    if (autoTriggeredForRef.current === key) return;
    const t = setTimeout(() => {
      autoTriggeredForRef.current = key;
      setShowRecognizeVerify(true);
    }, 700);
    return () => clearTimeout(t);
  }, [phone, email, recognize, recognizableChannel, recognized, isWalkInMode]);

  // Live-acceptance correction (Aug 2026), Correction 1 — a phone
  // number entered BEFORE SMS consent is checked deliberately does
  // NOT auto-send a code the moment consent is later checked
  // (`welcome_modal_sms_consent_gate.test.js`'s own regression test
  // proves this is required: checking the box must never itself be a
  // send trigger — see the auto-trigger effect's comment above for the
  // full rationale, reaffirmed unchanged by this correction). What
  // WAS unnecessary friction in that path: after checking consent, the
  // customer had to visually re-locate the "Verify" button that just
  // replaced the "check consent" text. This moves keyboard/focus
  // attention straight to it — one Enter/Space press away from
  // sending — without the checkbox itself issuing any network call.
  // Only fires on a genuine false→true transition (never on initial
  // mount, never when the button was already visible), so it can't
  // steal focus from an unrelated interaction.
  const verifyButtonRef = React.useRef(null);
  const prevSmsConsentRef = React.useRef(smsConsent);
  React.useEffect(() => {
    const becameChecked = smsConsent && !prevSmsConsentRef.current;
    prevSmsConsentRef.current = smsConsent;
    if (becameChecked && recognizableChannel === "sms" && !recognized?.customer) {
      verifyButtonRef.current?.focus();
    }
  }, [smsConsent, recognizableChannel, recognized]);

  async function handleRecognizeVerified(tokens) {
    setShowRecognizeVerify(false);
    const channel = recognizableChannel;
    const verified = channel && tokens?.[channel];
    if (!verified?.token) return;
    const destinationValue = channel === "sms" ? phone : (email || "").trim();
    // UX correction (Aug 2026), §C — this SAME proof is reused, never
    // re-verified, by "View my past orders" (`PastOrdersLookup`'s
    // `verifiedProof` prop below). The backend independently re-checks
    // freshness/scope on every call this proof is used for — this is
    // a UI convenience, never a trust decision made client-side.
    const proof = {
      channel, token: verified.token, destinationValue,
      catalogId: catalog?.id, verifiedAt: verified.verifiedAt || new Date().toISOString(),
    };
    setVerifiedProof(proof);
    onVerifiedProof && onVerifiedProof(proof);
    // The welcome-step OTP is a loyalty proof as well. Reported through
    // the same narrow channel so checkout has ONE source of truth for
    // "a proof the member completed", whichever flow produced it.
    onLoyaltyProof && onLoyaltyProof(proof);
    const recognizedResult = await runRecognize({
      phone, email,
      checkout_attempt_token: verified.token,
      destination_channel: channel,
      destination_value: destinationValue,
    });
    // Level 1 — Tenant Membership Foundation (Aug 2026). Enrollment
    // fires ONLY here: after the checkbox was explicitly checked AND
    // this exact OTP proof just succeeded — never on verification
    // alone, never merely from placing an order. Skipped entirely if
    // the customer is already an active member (nothing to do) so a
    // repeat visit never re-submits — enroll() is idempotent regardless,
    // this just avoids a redundant network call.
    if (membershipConsent && membershipEnabled && enrollMembership && !recognizedResult?.member) {
      setMembershipEnrollError(null);
      try {
        const enrolled = await enrollMembership({
          name,
          checkout_attempt_token: verified.token,
          destination_channel: channel,
          destination_value: destinationValue,
          consent_text_version: MEMBERSHIP_CONSENT_TEXT_VERSION,
          consent_source_url: typeof window !== "undefined" ? window.location.href : null,
        });
        if (enrolled && enrolled.member_number) {
          setMembershipJustEnrolled(enrolled);
          // Customer Favorites (Aug 2026) — `recognizedResult.member`
          // reflected the PRE-enrollment status (null/inactive, or
          // this branch wouldn't have run). Refresh the lifted
          // `recognizedRef` on the parent so Favorites becomes usable
          // on the very SAME visit, without waiting for a second
          // recognize() round trip.
          onRecognized && onRecognized({
            ...(recognizedResult || {}),
            member: { status: "active", member_number: enrolled.member_number },
          });
        }
      } catch (_e) {
        // Never block checkout on a failed enrollment — surface a
        // quiet, non-blocking notice and let ordering continue.
        setMembershipEnrollError("We couldn't complete membership enrollment. You can continue your order normally.");
      }
    }
  }

  // Final Operator-Validation Prerequisites correction (Aug 2026,
  // Product Owner final OTP decision) — the no-OTP counterpart to
  // `handleRecognizeVerified`'s enrollment call above. "Joining
  // membership" is explicitly one of the walk-in actions Rudras OTP
  // must never gate, so this fires on "Start Sales Order" itself for
  // Dine-In/Takeout — mirroring the SAME guard (checkbox checked,
  // Membership enabled, not already an active member) but with a
  // `phone` identity instead of an OTP proof. Deliberately
  // fire-and-forget (never awaited by `submit()` below) so a slow or
  // failed enrollment call can never delay or block placing an
  // ordinary order — exactly the same fail-open discipline
  // `checkSafeStatus` already established for this walk-in flow.
  const submitSafeEnrollmentIfNeeded = () => {
    if (!isWalkInMode || !membershipConsent || !membershipEnabled || !enrollMembershipSafe) return;
    if (safeMembershipActive) return; // already a member — nothing to do
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) return; // membership requires a phone identity, mirroring the OTP path's own "sms only" rule
    setMembershipEnrollError(null);
    enrollMembershipSafe({
      name,
      phone: trimmedPhone,
      consent_text_version: MEMBERSHIP_CONSENT_TEXT_VERSION,
      consent_source_url: typeof window !== "undefined" ? window.location.href : null,
    }).then((enrolled) => {
      if (enrolled && enrolled.member_number) {
        setMembershipJustEnrolled(enrolled);
        setSafeMembershipActive(true);
        onSafeRecognized?.({ active: true, phone: trimmedPhone, email: null });
      }
    }).catch(() => {
      setMembershipEnrollError("We couldn't complete membership enrollment. You can continue your order normally.");
    });
  };
  const [tableNumber, setTableNumber] = useState(initial?.tableNumber || "");
  // Iter45 / Step 2 — structured delivery address replaces the legacy
  // free-text textarea. The initial value may still be a string (older
  // session-stored customer); convert at first paint.
  const initialAddr = initial?.deliveryAddress;
  const [deliveryAddress, setDeliveryAddress] = useState(
    initialAddr && typeof initialAddr === "object"
      ? initialAddr
      : { line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", instructions: "" }
  );
  const updateAddr = (key) => (e) => {
    setDeliveryAddress((prev) => ({ ...prev, [key]: e.target.value }));
    // Phase 4 — any field edit invalidates a prior validation/suggestion;
    // the next submit must re-validate rather than reuse a stale result.
    setShippingValidation((v) => (v.status === "idle" ? v : { status: "idle", suggestion: null, message: "" }));
    if (orderType === "shipping" && validatedFingerprintRef.current) {
      logCustomerAuditEvent(slug, { event: "address_changed_post_validation", orderType, catalogId: catalog?.id, sessionId });
      validatedFingerprintRef.current = null;
    }
    // Phase 3A.5 Final Checkout Integration correction — a material
    // address edit clears the validation record ID, normalized address,
    // coordinates, and confirmation state, not just the fingerprint ref.
    // Applies whether the prior attempt was confirmed, still pending
    // confirmation, or outright rejected — every one of those is stale
    // the instant the underlying address changes.
    if (orderType === "delivery" && localDeliveryValidation.status !== "idle") {
      logCustomerAuditEvent(slug, { event: "address_changed_post_validation", orderType, catalogId: catalog?.id, sessionId });
      localDeliveryValidatedFingerprintRef.current = null;
      setLocalDeliveryValidation(LOCAL_DELIVERY_VALIDATION_IDLE);
    }
  };
  // Sprint B UX correction (Feb 2026):
  //   addressMode = "saved" → row from customers.addresses[] auto-filled.
  //   addressMode = "new"   → typing a fresh address; we ask whether to save.
  // savePref ∈ "default" | "additional" | "none" — forwarded to backend
  //   so it knows whether to upsert into customers.addresses[].
  const [addressMode, setAddressMode] = React.useState("saved");
  // Sprint B UX correction (Feb 2026) — default to "none" (privacy-friendly).
  // A customer may be ordering for a friend, family, office, or hotel; we
  // never permanently mutate their address book unless they opt in.
  const [savePref, setSavePref] = React.useState("none");
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  // Customer Identity, Address & Storefront Modal Reliability — Phase 4.
  // Parcel Shipping's address must be validated BEFORE the customer can
  // proceed to product selection — idle | checking | needs_confirmation |
  // error, same state machine ShipToAddressPanel already runs at
  // checkout (shared helpers, see lib/shipToAddressValidation.js). Any
  // edit to the address after a successful validation invalidates it —
  // enforced via `validatedFingerprintRef` below, never by re-running the
  // network call from a stale closure.
  const [shippingValidation, setShippingValidation] = useState({ status: "idle", suggestion: null, message: "" });
  const validatedFingerprintRef = React.useRef(null);
  // Phase 3A.5 Final Checkout Integration correction — the same pattern
  // for Local Delivery, tenant-flag-gated (`localDeliveryAddressValidationEnabled`).
  // A confirmed address requires BOTH a successful /validate call AND an
  // explicit customer confirm click (never auto-accepted the way an
  // unchanged Parcel Shipping address can be) — the backend's `/confirm`
  // route is the only place `status` can ever become "confirmed".
  const [localDeliveryValidation, setLocalDeliveryValidation] = useState(LOCAL_DELIVERY_VALIDATION_IDLE);
  const localDeliveryValidatedFingerprintRef = React.useRef(null);

  const rules = orderModes?.[orderType] || {};

  // Phase 1 (Modal Reliability) — report the in-progress (unsubmitted)
  // form back to the parent on every change. The parent keeps this in a
  // ref (not state) and folds it into `initial` the next time this
  // component mounts, so a deliberate Escape/Back close-and-reopen no
  // longer discards what the customer already typed. Backdrop-click can
  // no longer close the modal at all (see StorefrontModal), which closes
  // off the *accidental* data-loss path this same requirement covers.
  React.useEffect(() => {
    onDraftChange?.({ name, phone, email, orderType, tableNumber, deliveryAddress, smsConsent });
  }, [onDraftChange, name, phone, email, orderType, tableNumber, deliveryAddress, smsConsent]);

  // PR #41 hardening — Phase 3 (dependency invalidation matrix, section
  // A). Prior to this pass, `recognized` was set on a successful match
  // but NEVER cleared when the customer went on to type a DIFFERENT
  // phone/email that no longer identifies them — the stale "Welcome
  // back" banner, saved-address picker, and any already-validated
  // shipping address stayed attributed to the OLD customer. Runs on
  // every phone/email keystroke; a no-op unless a real recognition is
  // currently held AND neither channel it was matched on still agrees
  // with what's now typed.
  React.useEffect(() => {
    const rc = recognized?.customer;
    if (!rc) return;
    const checks = [];
    // E.164-normalized comparison (not a raw digit-string compare) — a
    // stored "+18325551234" and a freshly-typed "832-555-1234" are the
    // SAME number and must never be treated as a changed identity.
    if (rc.phone) checks.push(toE164(phone) === toE164(rc.phone));
    if (rc.email) checks.push(normalizeEmail(email) === normalizeEmail(rc.email));
    // No channel to compare against (shouldn't happen — a match always
    // came from at least one) — never reset on a comparison we can't
    // actually make.
    if (checks.length === 0) return;
    if (checks.some(Boolean)) return; // at least one channel still agrees — still the same customer

    setRecognized(null);
    onRecognized?.(null);
    // UX correction (Aug 2026) — the verification grant is scoped to
    // the EXACT phone/email it was issued for; once identity changes,
    // the stale proof must never be usable to retrieve a different
    // number's saved details or order history. Also closes any open
    // past-orders overlay, which would otherwise keep showing the OLD
    // customer's already-fetched orders.
    setVerifiedProof(null);
    setShowPastOrders(false);
    setShowLoyaltyPoints(false);
    logCustomerAuditEvent(slug, { event: "customer_changed", orderType, reasonCode: "identity_changed", catalogId: catalog?.id, sessionId, customerId: rc.id });
    // The saved-address selection only gets cleared if it actually came
    // FROM the stale customer (address_id present); free-typed text the
    // customer already entered is left alone — an identity change must
    // not silently discard an in-progress manual address entry.
    if (deliveryAddress.address_id) {
      setDeliveryAddress({
        address_id: "", line1: "", line2: "", city: "",
        state: "", postal_code: "", country: "US", instructions: "",
      });
      setAddressMode("new");
      logCustomerAuditEvent(slug, { event: "saved_address_reset", orderType, reasonCode: "identity_changed", catalogId: catalog?.id, sessionId, customerId: rc.id });
    }
    // Auto-filled name is misattributed once identity changes — but
    // only clear it if the customer never overrode the auto-fill (an
    // exact match to the stale recognized name); a manually-typed name
    // is the customer's own input and must survive.
    if (name && rc.name && name === rc.name) setName("");
    if (validatedFingerprintRef.current) {
      validatedFingerprintRef.current = null;
      setShippingValidation({ status: "idle", suggestion: null, message: "" });
      logCustomerAuditEvent(slug, { event: "address_validation_invalidated", orderType, reasonCode: "identity_changed", catalogId: catalog?.id, sessionId, customerId: rc.id });
    }
    // Phase 3A.5 Final Checkout Integration correction — same
    // invalidation for Local Delivery's validation state.
    if (localDeliveryValidatedFingerprintRef.current || localDeliveryValidation.status !== "idle") {
      localDeliveryValidatedFingerprintRef.current = null;
      setLocalDeliveryValidation(LOCAL_DELIVERY_VALIDATION_IDLE);
      logCustomerAuditEvent(slug, { event: "address_validation_invalidated", orderType, reasonCode: "identity_changed", catalogId: catalog?.id, sessionId, customerId: rc.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, email, recognized]);

  // Phase 3A.5 Final Checkout Integration correction — switching AWAY
  // from delivery must clear any in-progress/confirmed Local Delivery
  // validation. Without this, a customer who validates a delivery
  // address, switches to Pickup, then switches back to Delivery could
  // reach submit with a stale `localDeliveryValidation.status ===
  // "confirmed"` whose fingerprint happens to still match — this effect
  // guarantees a mode round-trip always forces a fresh validation.
  React.useEffect(() => {
    if (orderType === "delivery") return;
    if (localDeliveryValidatedFingerprintRef.current || localDeliveryValidation.status !== "idle") {
      localDeliveryValidatedFingerprintRef.current = null;
      setLocalDeliveryValidation(LOCAL_DELIVERY_VALIDATION_IDLE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  // Go-Live Sprint (Feb 2026, item #7 correction) — required contact
  // channels are STRICTLY tenant-driven. The previous hardcoded
  // "sprintARequiresEmail" and "sprintARequiresPhone" flags forced
  // email/phone for pickup/takeout/delivery regardless of the tenant's
  // Sales Order Modes configuration, which contradicted the approved
  // "Guest optional" contract. If a tenant genuinely wants a channel
  // required they set `require_phone` / `require_email` on the mode.

  // Phase 4 — final onSubmit call, factored out so both the normal submit
  // path and the post-validation "accept suggestion" / "keep as entered"
  // handlers build the exact same payload from current state.
  // `contactOverride` (Sep 2026) exists solely for the "Continue as
  // Guest" affordance below. It is NOT a second submit path: the guest
  // button runs this exact function and therefore builds the exact
  // same payload, it simply supplies blank contact details rather than
  // whatever is sitting in the form. Everything downstream — tenant
  // contact rules, OTP, SMS consent, membership — is untouched and
  // keeps deciding for itself, because a guest is just a customer who
  // gave no phone and no email, which this form already allows.
  const finalizeSubmit = (shipAddrForFingerprint, localDeliveryConfirmation, contactOverride) => {
    const guestName = contactOverride ? "" : name;
    const guestPhone = contactOverride ? "" : phone;
    const guestEmail = contactOverride ? "" : email;
    const guestConsent = contactOverride ? false : smsConsent;
    const cleanName = guestName.trim();
    const consentActive = !!guestConsent && !!guestPhone.trim();
    const sourceUrl = (typeof window !== "undefined" && window.location) ? window.location.href : "";
    // PR #41 hardening (Phase 2 — validated address as session context).
    // `validatedFingerprintRef` alone dies with this component on
    // unmount; stamping the same fingerprint + a timestamp onto the
    // persisted `shippingAddress` means "was this validated, and when"
    // survives as real session context (session storage via
    // RetailCustomer.jsx's `customer` record) instead of living only in
    // a local ref — reusable by checkout, order creation, and future
    // consumers without re-deriving it.
    let validationStamp = null;
    if (shipAddrForFingerprint) {
      validatedFingerprintRef.current = addressFingerprint(shipAddrForFingerprint);
      validationStamp = { fingerprint: validatedFingerprintRef.current, validated_at: new Date().toISOString() };
    }
    onSubmit({
      name: cleanName,
      phone: guestPhone.trim(),
      email: normalizeEmail(guestEmail),
      orderType,
      tableNumber: tableNumber.trim(),
      // Iter55 — A2P 10DLC consent capture. Only meaningful when a
      // phone number was supplied; we still record the bool either
      // way so the order trail is unambiguous.
      smsConsent: consentActive,
      smsConsentAt: consentActive ? new Date().toISOString() : null,
      // P1.1 — additional envelope fields captured client-side.
      smsConsentSourceUrl: consentActive ? sourceUrl.slice(0, 512) : null,
      smsConsentTextVersion: consentActive ? "v1.0" : null,
      deliveryAddress: (orderType === "delivery" && !hostLocked)
        ? {
            // Sprint B UX correction — forward the saved-address row id
            // (when picking) + the customer's "save this?" preference so
            // the backend knows whether to upsert into customers.addresses[].
            address_id: deliveryAddress.address_id || null,
            save_address: addressMode === "saved" ? "default" : savePref,
            line1: deliveryAddress.line1?.trim() || "",
            line2: deliveryAddress.line2?.trim() || "",
            city: deliveryAddress.city?.trim() || "",
            state: deliveryAddress.state?.trim() || "",
            postal_code: deliveryAddress.postal_code?.trim() || "",
            country: (deliveryAddress.country || "").trim().toUpperCase(),
            instructions: deliveryAddress.instructions?.trim() || "",
          }
        : null,
      // Phase 3A.5 Final Checkout Integration correction — set ONLY when
      // the /confirm call for THIS exact address just succeeded
      // (`confirmLocalDeliveryAddressAndFinalize` below passes the fresh
      // backend response, never a value derived from component state
      // that could lag a render). `CheckoutModal.jsx` already reads
      // `customer.addressValidationToken` — this is what finally
      // supplies it a live value instead of the previously-dead read.
      // `null` for every non-delivery mode and for delivery orders where
      // the tenant hasn't enabled address validation, fully backward
      // compatible.
      addressValidationToken: (orderType === "delivery" && localDeliveryConfirmation)
        ? localDeliveryConfirmation.checkoutAttemptToken
        : null,
      // Phase 4 — the customer's Parcel Shipping address, collected and
      // validated here (BEFORE product selection), in the same shape
      // CheckoutModal's ShipToAddressPanel/ShipToAddressForm already use
      // (`customer.shippingAddress`), so it autofills checkout without
      // any translation there. Editing it at checkout re-runs this same
      // validate-address call before rates can be fetched — see
      // ShipToAddressPanel.jsx, unchanged by this phase.
      shippingAddress: orderType === "shipping"
        ? {
            address_id: deliveryAddress.address_id || null,
            save_address: addressMode === "saved" ? "default" : savePref,
            name: cleanName, phone: phone.trim(), email: normalizeEmail(email),
            street1: deliveryAddress.line1?.trim() || "",
            street2: deliveryAddress.line2?.trim() || "",
            city: deliveryAddress.city?.trim() || "",
            region: (deliveryAddress.state || "").trim().toUpperCase(),
            postal: deliveryAddress.postal_code?.trim() || "",
            country: (deliveryAddress.country || "US").trim().toUpperCase(),
            // PR #41 hardening — validation provenance, carried forward
            // as session context (see comment above). `null` when this
            // shipping address was submitted without going through
            // validation (should not happen on the live storefront —
            // `submit()` fails closed on a missing `validateShippingAddress`
            // — but never fabricated as truthy just because a value exists).
            validation: validationStamp,
          }
        : null,
    });
  };

  // Phase 4 — runs the shared validate-address call (same endpoint/shape
  // ShipToAddressPanel uses at checkout) and either finalizes the submit,
  // or surfaces a blocking error/suggestion the customer must resolve
  // before product selection is reachable.
  const runShippingValidation = async (addr) => {
    setShippingValidation({ status: "checking", suggestion: null, message: "" });
    try {
      const result = await validateShippingAddress(welcomeAddressToValidateBody(addr, name.trim()));
      if (result?.detail?.code) {
        // Structured backend error unrelated to the address itself (e.g.
        // parcel_shipping_unavailable) — never block product selection
        // on a validation-layer problem; the customer will see a clearer
        // error at checkout if shipping is genuinely unavailable.
        setShippingValidation({ status: "idle", suggestion: null, message: "" });
        finalizeSubmit(addr);
        return;
      }
      const suggestion = result?.suggestion || null;
      const bodyForCompare = welcomeAddressToValidateBody(addr, name.trim());
      if (result?.ok !== false && suggestionDiffersFromSubmitted(bodyForCompare, suggestion)) {
        setShippingValidation({ status: "needs_confirmation", suggestion, message: (result.messages || [])[0] || "" });
        return;
      }
      if (result?.ok === false) {
        setShippingValidation({
          status: "error", suggestion: null,
          message: (result.messages || [])[0] || "We couldn't verify this address. Please double-check it.",
        });
        return;
      }
      setShippingValidation({ status: "idle", suggestion: null, message: "" });
      finalizeSubmit(addr);
    } catch (_e) {
      // Network/unexpected failure in the validation step itself must
      // never trap the customer — same fail-open behavior as
      // ShipToAddressPanel's checkout-time validation.
      setShippingValidation({ status: "idle", suggestion: null, message: "" });
      finalizeSubmit(addr);
    }
  };

  const acceptShippingSuggestion = () => {
    const merged = { ...deliveryAddress, ...suggestionToWelcomeShape(shippingValidation.suggestion) };
    setDeliveryAddress(merged);
    setShippingValidation({ status: "idle", suggestion: null, message: "" });
    logCustomerAuditEvent(slug, { event: "suggestion_accepted", orderType, reasonCode: "carrier_suggested_correction", catalogId: catalog?.id, sessionId, customerId: recognized?.customer?.id });
    finalizeSubmit(merged);
  };

  const keepShippingAddressAsEntered = () => {
    setShippingValidation({ status: "idle", suggestion: null, message: "" });
    logCustomerAuditEvent(slug, { event: "suggestion_declined", orderType, catalogId: catalog?.id, sessionId, customerId: recognized?.customer?.id });
    finalizeSubmit(deliveryAddress);
  };

  // Phase 3A.5 Final Checkout Integration correction — calls
  // POST .../local-delivery/address/validate exactly once per click (no
  // internal retry loop in this milestone — a definitive provider
  // rejection or network failure both surface as a blocking state the
  // customer must explicitly retry from). Never falls through to
  // `finalizeSubmit` on any outcome other than an explicit customer
  // confirm click below — this pipeline is fail-CLOSED (Phase 3A.5 §14),
  // unlike `runShippingValidation`'s fail-open network-error handling.
  const runLocalDeliveryValidation = async (addr) => {
    const priorToken = localDeliveryValidation.checkoutAttemptToken;
    setLocalDeliveryValidation({ ...LOCAL_DELIVERY_VALIDATION_IDLE, status: "checking", checkoutAttemptToken: priorToken });
    const body = {
      catalog_id: catalog?.id || null,
      checkout_attempt_token: priorToken || null,
      address: {
        line1: addr.line1?.trim() || "",
        line2: addr.line2?.trim() || "",
        city: addr.city?.trim() || "",
        state: addr.state?.trim() || "",
        postal_code: addr.postal_code?.trim() || "",
        country: (addr.country || "US").trim().toUpperCase(),
        instructions: addr.instructions?.trim() || "",
      },
    };
    const result = await validateLocalDeliveryAddress(body);
    if (!result || result.ok === false) {
      // `address_structurally_invalid` carries per-field messages —
      // surface them the same way the presence-check above does, rather
      // than only the generic banner.
      if (result?.fields && typeof result.fields === "object" && Object.keys(result.fields).length > 0) {
        setFieldErrors(result.fields);
      }
      setLocalDeliveryValidation({
        ...LOCAL_DELIVERY_VALIDATION_IDLE,
        status: "error",
        checkoutAttemptToken: (result && result.checkout_attempt_token) || priorToken || null,
        reasonCode: (result && result.code) || null,
        message: (result && result.message) || "We couldn't validate that address right now. Please try again.",
      });
      logCustomerAuditEvent(slug, { event: "local_delivery_address_validation_failed", orderType, reasonCode: (result && result.code) || "unknown", catalogId: catalog?.id, sessionId });
      return;
    }
    setFieldErrors({});
    const nextState = {
      status: result.status === "invalid_or_imprecise" ? "invalid" : "needs_confirmation",
      validationId: result.validation_id,
      checkoutAttemptToken: result.checkout_attempt_token,
      normalizedAddress: result.normalized_address,
      latitude: result.latitude,
      longitude: result.longitude,
      serverFingerprint: result.address_fingerprint,
      serviceabilityStatus: result.serviceability_status,
      reasonCode: result.reason_code,
      message: result.status === "invalid_or_imprecise" ? localDeliveryReasonMessage(result.reason_code) : "",
    };
    setLocalDeliveryValidation(nextState);
    logCustomerAuditEvent(slug, {
      event: nextState.status === "invalid" ? "local_delivery_address_rejected" : "local_delivery_address_validated",
      orderType, reasonCode: result.reason_code || null, catalogId: catalog?.id, sessionId,
    });
  };

  // Explicit customer confirmation click — the ONLY place `status` can
  // become "confirmed" and the ONLY place `finalizeSubmit` is called for
  // a validated Local Delivery address. Builds the outgoing payload from
  // the FRESH backend response (`result`), never from state that may not
  // have re-rendered yet, avoiding the stale-closure trap called out in
  // `submitRef`'s comment in CheckoutModal.jsx.
  const confirmLocalDeliveryAddressAndFinalize = async () => {
    if (!localDeliveryValidation.validationId || !localDeliveryValidation.serverFingerprint) return;
    setLocalDeliveryValidation((v) => ({ ...v, status: "confirming" }));
    const result = await confirmLocalDeliveryAddress({
      validation_id: localDeliveryValidation.validationId,
      address_fingerprint: localDeliveryValidation.serverFingerprint,
    });
    if (!result || result.ok === false || result.status !== "verified") {
      setLocalDeliveryValidation((v) => ({
        ...v,
        status: "error",
        message: (result && result.message) || "This address needs to be re-validated before it can be confirmed.",
      }));
      logCustomerAuditEvent(slug, { event: "local_delivery_address_confirm_failed", orderType, reasonCode: (result && result.code) || "unknown", catalogId: catalog?.id, sessionId });
      return;
    }
    localDeliveryValidatedFingerprintRef.current = addressFingerprint(deliveryAddress);
    setLocalDeliveryValidation({
      status: "confirmed",
      validationId: result.validation_id,
      checkoutAttemptToken: result.checkout_attempt_token,
      normalizedAddress: result.normalized_address,
      latitude: result.latitude,
      longitude: result.longitude,
      serverFingerprint: result.address_fingerprint,
      serviceabilityStatus: result.serviceability_status,
      reasonCode: result.reason_code,
      message: "",
    });
    logCustomerAuditEvent(slug, { event: "local_delivery_address_confirmed", orderType, catalogId: catalog?.id, sessionId });
    finalizeSubmit(null, { checkoutAttemptToken: result.checkout_attempt_token });
  };

  const submit = async (e, opts) => {
    e?.preventDefault?.();
    setError("");
    const asGuest = !!(opts && opts.asGuest);
    if (asGuest) {
      // Clear what the customer may have half-typed so the form
      // visibly matches what is being sent, then go straight through
      // the ordinary finalize path with blank contact details.
      setName(""); setPhone(""); setEmail(""); setSmsConsent(false);
      finalizeSubmit(null, null, { guest: true });
      return;
    }
    const cleanName = name.trim();
    // Name is required ONLY when the tenant explicitly required a
    // contact channel. Otherwise blank name is fine — the backend
    // defaults it to "Guest".
    const nameRequired = !!(rules.require_phone || rules.require_email);
    if (nameRequired && !cleanName) {
      setError("Please enter your name.");
      return;
    }
    if (rules.require_phone && !phone.trim()) {
      setError("Please add a phone number for this order type.");
      return;
    }
    if (rules.require_email && !email.trim()) {
      setError("Please add an email so we can reach you.");
      return;
    }
    if (email.trim() && !isValidEmail(email)) {
      setError("Enter a valid email address.");
      logCustomerAuditEvent(slug, { event: "contact_email_validation_failed", orderType, reasonCode: "email_invalid_format", catalogId: catalog?.id, sessionId });
      return;
    }
    // Contact Validation & Messaging Configuration Recovery — this
    // check previously only ran when `require_phone` was set, so a
    // catalog with phone marked OPTIONAL still let a nonblank-but-
    // incomplete number ("882-314-0") through untouched. A blank phone
    // stays fine wherever it's optional; a nonblank one must be
    // complete regardless of whether the tenant requires it.
    if (phone.trim() && !isValidPhone(phone)) {
      setError("Enter a complete 10-digit mobile number.");
      logCustomerAuditEvent(slug, { event: "contact_phone_validation_failed", orderType, reasonCode: "phone_incomplete", catalogId: catalog?.id, sessionId });
      return;
    }
    // `require_phone_or_email` is a tenant-config LABEL — per approved
    // rule it does NOT require at least one channel. Both may be blank.
    // Hospitality H1 — the front desk matches food to a guest by name.
    if (hostLocked && !name.trim()) {
      setError("Please enter your name so the front desk can match your order.");
      return;
    }
    if (orderType === "delivery" && rules.require_address && !hostLocked) {
      const missing = {};
      if (!deliveryAddress.line1?.trim()) missing.line1 = "Street address is required.";
      if (!deliveryAddress.city?.trim()) missing.city = "City is required.";
      if (!deliveryAddress.state?.trim()) missing.state = "State / region is required.";
      if (!deliveryAddress.postal_code?.trim()) missing.postal_code = "Postal / ZIP code is required.";
      if (!deliveryAddress.country?.trim()) missing.country = "Country is required.";
      if (Object.keys(missing).length) {
        setFieldErrors(missing);
        setError("Please complete the delivery address.");
        return;
      }
    }
    // Phase 4 — Parcel Shipping always needs a real address up front
    // (unlike delivery, this is never tenant-optional: there is no such
    // thing as a Parcel Shipping order with no destination).
    if (orderType === "shipping") {
      const missing = {};
      if (!deliveryAddress.line1?.trim()) missing.line1 = "Street address is required.";
      if (!deliveryAddress.city?.trim()) missing.city = "City is required.";
      if (!deliveryAddress.state?.trim()) missing.state = "State / region is required.";
      if (!deliveryAddress.postal_code?.trim()) missing.postal_code = "Postal / ZIP code is required.";
      if (!deliveryAddress.country?.trim()) missing.country = "Country is required.";
      if (Object.keys(missing).length) {
        setFieldErrors(missing);
        setError("Please complete the shipping address.");
        return;
      }
      setFieldErrors({});
      const fp = addressFingerprint(deliveryAddress);
      if (validatedFingerprintRef.current === fp && shippingValidation.status === "idle") {
        // Unchanged since it was last successfully validated — no need
        // to spend another carrier call.
        finalizeSubmit(deliveryAddress);
        return;
      }
      if (!validateShippingAddress) {
        // No validation wired in (should not happen on the live
        // storefront) — never silently skip; fail closed with a clear
        // error rather than letting an unvalidated address through.
        setError("Address validation is unavailable right now. Please try again shortly.");
        return;
      }
      await runShippingValidation(deliveryAddress);
      return;
    }
    // Phase 3A.5 Final Checkout Integration correction — Local Delivery
    // address validation, tenant-flag-gated. Runs AFTER the presence
    // check above (line ~423) so an incomplete address never spends a
    // Google Address Validation call. Every branch below either blocks
    // with a clear reason or returns early pending the customer's
    // explicit confirm click in the panel rendered below — this
    // function-level `submit()` never itself marks a delivery address
    // confirmed.
    if (orderType === "delivery" && localDeliveryAddressValidationEnabled && !hostLocked) {
      setFieldErrors({});
      const fp = addressFingerprint(deliveryAddress);
      if (localDeliveryValidatedFingerprintRef.current === fp && localDeliveryValidation.status === "confirmed") {
        // Unchanged since the customer's last explicit confirm — no need
        // to spend another Google call. In practice `finalizeSubmit`
        // already advanced past this modal the moment confirmation
        // succeeded; this guards a resubmit before that state change is
        // reflected.
        finalizeSubmit(null, { checkoutAttemptToken: localDeliveryValidation.checkoutAttemptToken });
        return;
      }
      if (!validateLocalDeliveryAddress || !confirmLocalDeliveryAddress) {
        // No validation wired in (should not happen when the tenant flag
        // is on) — fail closed rather than let an unvalidated address
        // through.
        setError("Address validation is unavailable right now. Please try again shortly.");
        return;
      }
      if (localDeliveryValidation.status !== "idle") {
        // checking / needs_confirmation / invalid / confirming / error —
        // all of these must be resolved via the panel's own buttons
        // below, never by re-submitting the whole form (which would
        // spend a second Google call for the same address).
        return;
      }
      await runLocalDeliveryValidation(deliveryAddress);
      return;
    }
    setFieldErrors({});
    submitSafeEnrollmentIfNeeded();
    finalizeSubmit(null);
  };

  // Go-Live Enhancement (Feb 2026, Dine-In / Takeout optional contact
  // capture) — Phone and Email fields are now shown for ALL order
  // modes. Whether they're required or optional is driven purely by
  // the tenant's per-mode rules (`require_phone` / `require_email`).
  // Blank values remain blank (never converted to placeholders) and
  // are accepted end-to-end.
  const showPhone = true;
  const showEmail = true;

  return (
    <>
    {showPastOrders && (
      <PastOrdersLookup
        slug={slug}
        catalogId={catalog?.id}
        phoneAccessAllowed={!storefrontRequiresLoyaltyOtp(catalog)}
        sessionId={sessionId}
        verifiedProof={verifiedProof}
        // Loyalty checkout no-request fix (Sep 2026) — Past Orders is
        // the only place a Dine-In/Takeout customer completes a real
        // OTP, so the proof it obtains has to reach the page the same
        // way `handleRecognizeVerified`'s does. No second code is ever
        // sent: this is the proof the customer just completed.
        // Loyalty checkout no-request fix (Sep 2026) — lift this proof
        // for LOYALTY ONLY.
        //
        // Deliberately NOT `onVerifiedProof`, and deliberately not
        // routed into the page's `verifiedProofRef`. That ref is the
        // Favorites authorization boundary, and `PastOrdersLookup`'s
        // proof is scoped to `order_lookup`; the two are kept
        // unconflated on purpose (see
        // `retail_customer_favorites_proof_transfer.test.js`, which
        // asserts exactly that separation). This adds a third,
        // narrower consumer instead of widening an existing grant.
        //
        // It matters because for Dine-In/Takeout this is the ONLY real
        // OTP the storefront offers — the welcome step's auto-OTP and
        // its "Verify" button are both scoped out of walk-in mode — so
        // without it a walk-in member who genuinely verified still
        // reached checkout with no proof, and the loyalty panel issued
        // no request at all. No second code is ever sent: this is the
        // proof the customer just completed.
        onLoyaltyProof={(p) => {
          if (!p?.token) return;
          onLoyaltyProof && onLoyaltyProof({
            channel: p.channel || "sms",
            token: p.token,
            destinationValue: p.destinationValue || null,
            catalogId: catalog?.id,
            verifiedAt: new Date().toISOString(),
          });
        }}
        onClose={() => {
          setShowPastOrders(false);
          const trigger = pastOrdersTriggerRef.current;
          if (trigger) {
            // Deferred a frame: the dialog is still unmounting on this
            // tick, and Radix's own restore would otherwise land after
            // (and override) an immediate focus() call.
            requestAnimationFrame(() => { try { trigger.focus(); } catch (_e) { /* detached */ } });
          }
        }}
        // Correct the Failed PR #140/#142 Walk-In Workflow (Sep 2026)
        // — the phone number/consent already established on THIS
        // form, so Past Orders never asks the customer to retype the
        // number or re-consent to something already on file. See
        // `PastOrdersLookup`'s own prop docstrings for the exact
        // reasoning on each.
        initialPhone={phone}
        initialSmsConsent={smsConsent}
      />
    )}
    {showLoyaltyPoints && (
      <LoyaltyPointsPanel
        slug={slug}
        catalogId={catalog?.id}
        sessionId={sessionId}
        verifiedProof={verifiedProof}
        onClose={() => setShowLoyaltyPoints(false)}
      />
    )}
    {/* Post-deployment security correction (Aug 2026) — the ONLY way
        `recognized.customer` can ever become populated now: a real,
        server-verified OTP proof, exactly like `PastOrdersLookup`'s own
        verification step. `purpose="order_lookup"` forces OTP REQUIRED
        regardless of the tenant's checkout policy (routes/retail_otp.py). */}
    {showRecognizeVerify && recognizableChannel && (
      <VerificationModal
        slug={slug}
        catalogId={catalog?.id}
        sessionId={sessionId}
        customer={{ phone, email, smsConsent }}
        requiredChannels={[recognizableChannel]}
        orderType="lookup"
        purpose="order_lookup"
        onClose={() => setShowRecognizeVerify(false)}
        onVerified={handleRecognizeVerified}
      />
    )}
    <StorefrontModal
      testId="retail-welcome-modal"
      onCancel={onCancel}
      onBackdropBlocked={() => logCustomerAuditEvent(slug, { event: "modal_close_blocked", orderType, reasonCode: "backdrop_click_blocked", catalogId: catalog?.id, sessionId })}
      title="Sales Order"
      description="Tell us a little about your visit"
      zIndexClassName="z-40"
    >
      <form onSubmit={submit} className="text-stone-900">
        <div className="flex items-center gap-3 mb-5">
          {/* Sales Order intro logo. The stored `tenant.logo_url` is a
              BACKEND-relative path, so it needs resolving against the API
              origin before a browser on the storefront origin can fetch
              it — see `lib/retailFormat.js::resolveTenantLogoUrl`.

              This is the fourth surface with the same defect and the one
              the Sep 2026 storefront pass (`a4876f2d`) did not reach: it
              fixed SalesOrderLanding, KioskShell and RetailChat but left
              this modal rendering the raw path. It is the screen the
              customer actually sees first when ordering, so it is fixed
              here with the same one rule and the same fallback. */}
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={`${tenant?.name || catalog?.name || "Restaurant"} logo`}
              className="w-12 h-12 rounded-lg object-contain bg-black"
              data-testid="retail-welcome-logo"
              onError={() => setLogoBroken(true)}
            />
          ) : (
            <div
              className="w-12 h-12 rounded-lg bg-emerald-900 grid place-items-center text-emerald-100"
              data-testid="retail-welcome-logo-fallback"
              role="img"
              aria-label={`${tenant?.name || catalog?.name || "Restaurant"} logo`}
            ><ShoppingBag className="w-5 h-5"/></div>
          )}
          <h2 className="text-2xl font-semibold">Sales Order 🙏</h2>
        </div>
        <p className="text-sm text-stone-500 mb-3">Tell us a little about your visit</p>

        {/* Continue as Guest (Sep 2026).
            Walk-in and kiosk customers could ALREADY order without
            identifying themselves — every contact field below is
            optional unless the tenant sets `require_phone` /
            `require_email`, and `retail/orders.py::_validate_customer`
            defaults a blank name to "Guest". What was missing was any
            sign of it.

            It sits HERE, above the first field, because that is the
            whole point: a customer at the counter should be able to
            leave without reading a form that opens on PHONE NUMBER
            above an SMS consent block and reads as mandatory. Below the
            Start button it was already too late — they would have
            worked through the fields to find it. Primary styling for
            the same reason: it is a real first-class way in, not a
            consolation.

            Rendered ONLY when this order type genuinely permits blank
            contact details, so it can never promise something the
            tenant's own rules then refuse. It changes no OTP, consent
            or membership logic — it runs the same `finalizeSubmit` the
            Start button runs, with the contact fields left empty. */}
        {!rules.require_phone && !rules.require_email && (
          <>
            <button
              type="button"
              data-testid="retail-welcome-guest"
              onClick={() => submit(null, { asGuest: true })}
              className="w-full h-12 rounded-lg bg-orange-500 hover:bg-orange-600 active:scale-[0.99] text-white font-semibold uppercase tracking-wider transition"
            >
              Continue as Guest
            </button>
            <p className="mt-2 mb-5 text-center text-xs text-stone-400">
              or tell us who you are, so we can find your order later
            </p>
          </>
        )}

        {/* Sprint B UX correction (Feb 2026) — Phone is the FIRST field
            on the Sales Order screen. Typing a recognised number
            auto-populates Name, Email, default saved address, and
            surfaces the active loyalty coupon. */}
        {showPhone && (
          <Field label="Phone number" optional={!rules.require_phone}>
            <input value={phone} onChange={(e) => setPhone(formatUsPhoneDisplay(e.target.value))} placeholder="e.g. 832-555-1234" inputMode="tel" data-testid="retail-welcome-phone"
              className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"/>
            {phone.trim() && !isValidPhone(phone) && (
              <p className="mt-1 text-xs text-rose-600" role="alert" data-testid="retail-welcome-phone-hint">
                Enter a complete 10-digit mobile number.
              </p>
            )}
            {/* Post-deployment security correction (Aug 2026) — replaces
                the old silent, unauthenticated "Welcome back" prefill.
                Shown once a channel is recognizable AND nothing has
                been verified yet for it; never shows the customer's
                name, email, address, or order history before that.
                UX correction (Aug 2026) — verification now starts
                AUTOMATICALLY (see the debounce effect above) the
                instant the number/email settles; this row is what
                remains visible while that's in flight, and is also the
                "clear retry control" if the customer closes the OTP
                dialog without finishing (the auto-trigger deliberately
                does not reopen itself for the same value). */}
            {/* Final Operator-Validation Prerequisites correction (Aug
                2026, Product Owner final OTP decision) — Dine-In/
                Takeout no longer shows the OTP-implying "Verify your
                phone number..." prompt at all: no verification step
                exists in the normal walk-in flow anymore. Replaced
                with context-sensitive, non-misleading copy — before any
                recognition, then a "no verification needed" welcome
                once the SAFE (non-OTP) check confirms an active
                membership — plus the ONE remaining Rudras-OTP entry
                point for walk-in customers, "View my past orders",
                always available once a channel is recognizable
                (previously ONLY appeared after a completed OTP, which
                made it unreachable without doing the very thing it
                exists to gate). Delivery/Shipping/Online Order keep the
                exact original prompt below, unchanged. */}
            {/* Duplicate "View my past orders" correction (Sep 2026) —
                ROOT CAUSE: this walk-in prompt and the verified
                `welcome-recognized-banner` below are two SEPARATE
                recognition panels, and this one was gated only on
                "a channel is recognizable". Once an OTP completed, both
                rendered at once, each with its own "View my past orders"
                button — so the customer saw the action twice, under two
                overlapping welcome panels. The non-walk-in prompt a few
                lines down already carried the `!recognized?.customer`
                gate; this one did not. Adding it lets the verified
                banner supersede this prompt, which is what it was always
                meant to do: the banner is a strict superset (welcome-back
                name, order count, last order, saved addresses, past
                orders, points, member badge). Nothing about verification,
                the OTP flow, or either panel's own content changes, and
                the walk-in OTP entry point stays reachable for a customer
                who has NOT verified — it is the only real OTP this flow
                offers. */}
            {isWalkInMode && recognizableChannel && !recognized?.customer && (
              <div
                className="mt-2 rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm"
                data-testid="welcome-safe-recognize-prompt"
              >
                {safeMembershipActive ? (
                  <p className="text-emerald-800 font-medium" data-testid="welcome-safe-recognized-banner">
                    Welcome back, Member. You can continue your order without verification.
                  </p>
                ) : (
                  <p className="text-stone-600" data-testid="welcome-safe-not-yet-recognized">
                    Phone number is optional. Enter it to apply existing membership preferences or receive order updates.
                  </p>
                )}
                <button
                  type="button"
                  onClick={(e) => { pastOrdersTriggerRef.current = e.currentTarget; setShowPastOrders(true); }}
                  data-testid="welcome-view-past-orders-walkin"
                  className="mt-2 text-xs font-semibold text-orange-700 underline hover:text-orange-900 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                >
                  View my past orders
                </button>
              </div>
            )}
            {!isWalkInMode && !recognized?.customer && recognizableChannel && (
              <div
                className="mt-2 rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm"
                data-testid="welcome-recognize-verify-prompt"
              >
                <p className="text-stone-600">
                  Verify your phone number to securely retrieve saved details and past orders.
                </p>
                {/* New-customer SMS consent fix (Aug 2026) — this button
                    used to open the OTP modal (and trigger a real SMS
                    send) for the "sms" channel regardless of the consent
                    checkbox below. It now stays a disabled, explanatory
                    prompt until consent is given for SMS; email has no
                    consent requirement and is unaffected. */}
                {recognizableChannel === "sms" && !smsConsent ? (
                  <p className="text-stone-500" data-testid="welcome-recognize-verify-needs-consent">
                    Check the SMS consent box below to verify by text message.
                  </p>
                ) : (
                  <button
                    ref={verifyButtonRef}
                    type="button"
                    onClick={() => setShowRecognizeVerify(true)}
                    data-testid="welcome-recognize-verify-button"
                    className="mt-2 text-xs font-semibold text-orange-700 underline hover:text-orange-900"
                  >
                    {showRecognizeVerify ? "Sending verification code…" : "Verify"}
                  </button>
                )}
              </div>
            )}
            {/* Level 1 — Tenant Membership Foundation (Aug 2026). A
                FRESH enrollment confirmation — deliberately independent
                of `recognized.customer` (a first-time visitor who just
                joined has no prior order history, so that block never
                renders for them) and shown ONLY after the backend
                itself confirmed the enrollment (`membershipJustEnrolled`
                is set from the enroll API's own response, never
                optimistically from the checkbox). No internal MongoDB
                id is ever surfaced — only the customer-facing member
                number. */}
            {membershipJustEnrolled && (
              <div
                className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm"
                data-testid="welcome-membership-activated-banner"
              >
                <div className="font-semibold text-emerald-900">Membership activated</div>
                <div className="text-[11px] text-emerald-700 font-mono mt-0.5" data-testid="welcome-membership-activated-id">
                  Member ID: {membershipJustEnrolled.member_number}
                </div>
              </div>
            )}
            {membershipEnrollError && (
              <div
                className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"
                data-testid="welcome-membership-enroll-error"
              >
                {membershipEnrollError}
              </div>
            )}
            {/* Phase 2A — returning customer / available coupon banner.
                Only ever rendered once `recognized.customer` is set by a
                genuinely OTP-verified `runRecognize` call above. */}
            {recognized && recognized.customer && (
              <div
                className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm"
                data-testid="welcome-recognized-banner"
              >
                <div className="font-semibold text-emerald-900" data-testid="welcome-recognized-name">
                  Welcome back, {recognized.customer.name || "there"}!
                </div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  {recognized.customer.last_order_date ? (
                    <>Last order: {new Date(recognized.customer.last_order_date).toLocaleDateString()}</>
                  ) : null}
                  {recognized.customer.total_orders ? (
                    <> · {recognized.customer.total_orders} order{recognized.customer.total_orders === 1 ? "" : "s"} with us</>
                  ) : null}
                  {recognized.customer.addresses?.length ? (
                    <> · {recognized.customer.addresses.length} saved address{recognized.customer.addresses.length === 1 ? "" : "es"}</>
                  ) : null}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <button
                    type="button"
                    onClick={(e) => { pastOrdersTriggerRef.current = e.currentTarget; setShowPastOrders(true); }}
                    data-testid="welcome-view-past-orders"
                    className="text-xs font-semibold text-emerald-700 underline hover:text-emerald-900 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                  >
                    View my past orders
                  </button>
                  {/* Loyalty Phase 1 (Sep 2026) — shown only inside this
                      banner, which the backend populates exclusively
                      after an OTP-verified proof. A recognized-but-
                      unverified customer never reaches here, so a typed
                      phone number can never surface a balance. */}
                  {recognized.member && (
                    <button
                      type="button"
                      onClick={() => setShowLoyaltyPoints(true)}
                      data-testid="welcome-view-loyalty-points"
                      className="text-xs font-semibold text-emerald-700 underline hover:text-emerald-900"
                    >
                      View my points
                    </button>
                  )}
                </div>
                {/* Level 1 — Tenant Membership Foundation (Aug 2026).
                    Returning-member recognition — rendered ONLY from
                    `recognized.member`, which the backend populates
                    exclusively after this same verified-proof check
                    already required for the rest of this banner. Never
                    shown from an entered phone number alone, and never
                    exposes anything beyond status + the existing
                    CUST-###### member number. */}
                {recognized.member && (
                  <div
                    className="mt-2 rounded-md bg-white border border-emerald-300 px-3 py-2"
                    data-testid="welcome-member-badge"
                  >
                    <div className="text-emerald-900 font-semibold">Welcome back, Member</div>
                    <div className="text-[11px] text-emerald-700 font-mono" data-testid="welcome-member-id">
                      {recognized.member.member_number}
                    </div>
                  </div>
                )}
                {recognized.coupon && (
                  <div className="mt-2 flex items-center justify-between rounded-md bg-white border border-emerald-300 px-3 py-2"
                       data-testid="welcome-coupon-banner">
                    <div>
                      <div className="text-emerald-900 font-semibold" data-testid="welcome-coupon-discount">
                        {recognized.coupon.discount_percent}% OFF available
                      </div>
                      <div className="text-[11px] text-emerald-700 font-mono" data-testid="welcome-coupon-code">
                        {recognized.coupon.coupon_code} · expires {new Date(recognized.coupon.expiration_date).toLocaleDateString()}
                      </div>
                    </div>
                    <span className="text-[11px] uppercase tracking-wider text-emerald-700">auto-applied</span>
                  </div>
                )}
              </div>
            )}
            {/* Iter55 — A2P 10DLC informational/transactional SMS consent.
                Optional, unchecked by default. We don't gate ordering on
                it; it just records a clean consent trail before any SMS
                is sent for that order. */}
            <label
              data-testid="retail-welcome-sms-consent-label"
              className="mt-2 flex items-start gap-2 text-xs text-stone-600 leading-snug cursor-pointer"
            >
              <input
                type="checkbox"
                data-testid="retail-welcome-sms-consent"
                checked={!!smsConsent}
                disabled={safeSmsConsentActive}
                aria-disabled={safeSmsConsentActive}
                onChange={(e) => { setSmsConsent(e.target.checked); setSmsConsentTouched(true); }}
                className={"mt-0.5 h-4 w-4 accent-orange-500 shrink-0 " + (safeSmsConsentActive ? "cursor-not-allowed" : "cursor-pointer")}
              />
              <span>
                I agree to receive order updates and verification codes via SMS. Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to opt out, <strong>HELP</strong> for help. See <a href="/privacy" target="_blank" rel="noopener" className="underline">Privacy Policy</a> &amp; <a href="/terms" target="_blank" rel="noopener" className="underline">Terms</a>.
                {/* Walk-in customer recognition/verification correction
                    (Aug 2026) — the required matrix's "Checked and
                    disabled" state for a returning customer with
                    already-active SMS consent. */}
                {safeSmsConsentActive && (
                  <span data-testid="retail-welcome-sms-consent-already-active" className="block mt-0.5 text-[10px] text-stone-400">
                    You&apos;ve already agreed to this.
                  </span>
                )}
              </span>
            </label>

            {/* Level 1 — Tenant Membership Foundation (Aug 2026).
                Deliberately a SEPARATE <label>/<input> from the SMS
                consent checkbox above — different state variable,
                different testid, own paragraph of copy — so the two
                consents can never be visually or semantically confused.
                Unchecked by default. Only rendered when this tenant has
                enabled Membership (`tenant.membership_config.enabled`).
                Makes no claim about any specific discount, points
                amount, or reward not yet implemented. */}
            {membershipEnabled && (
              <div className="mt-3">
                <label
                  data-testid="retail-welcome-membership-consent-label"
                  className={
                    "flex items-start gap-2 text-xs leading-snug " +
                    (smsConsent && !safeMembershipActive ? "text-stone-600 cursor-pointer" : "text-stone-400 cursor-not-allowed")
                  }
                >
                  <input
                    type="checkbox"
                    data-testid="retail-welcome-membership-consent"
                    checked={!!membershipConsent}
                    disabled={!smsConsent || safeMembershipActive}
                    aria-disabled={!smsConsent || safeMembershipActive}
                    // Live-acceptance correction (Aug 2026) — links the
                    // checkbox to the guidance paragraph below for
                    // screen readers, whether or not it's currently
                    // disabled (aria-describedby on an enabled control
                    // is harmless once the paragraph no longer renders,
                    // since the id simply won't exist).
                    aria-describedby={!smsConsent ? "retail-welcome-membership-needs-sms-consent" : undefined}
                    onChange={(e) => setMembershipConsent(e.target.checked)}
                    className={
                      "mt-0.5 h-4 w-4 accent-emerald-600 shrink-0 " +
                      (smsConsent && !safeMembershipActive ? "cursor-pointer" : "cursor-not-allowed")
                    }
                  />
                  <span>
                    {/* Walk-in customer recognition/verification
                        correction (Aug 2026) — an already-active member
                        sees a factual statement, never the "Join"
                        invitation copy (that would misleadingly imply
                        they aren't a member yet). */}
                    {safeMembershipActive ? (
                      <span data-testid="retail-welcome-membership-already-active">
                        You&apos;re already a member of this store&apos;s membership program.
                      </span>
                    ) : (
                      "Join this store's membership program and receive member benefits as this store introduces them. Optional — you can order normally either way."
                    )}
                  </span>
                </label>
                {/* Live-acceptance correction (Aug 2026) — Membership's
                    identity foundation is a verified mobile number, so
                    joining requires transactional SMS consent first.
                    Deliberately an ALWAYS-VISIBLE inline paragraph
                    (not a hover-only title/tooltip) the instant the
                    checkbox is disabled: a native `disabled` checkbox
                    does not reliably emit hover/focus/tap affordances
                    across desktop mouse, keyboard focus, and mobile
                    tap alike, so the guidance can't depend on any of
                    those interactions firing — it's simply always
                    there to read, for every input method, the moment
                    it's relevant. */}
                {!smsConsent && (
                  <p
                    id="retail-welcome-membership-needs-sms-consent"
                    data-testid="retail-welcome-membership-needs-sms-consent"
                    className="mt-1 pl-6 text-[11px] text-stone-500"
                  >
                    Please agree to receive verification and order-update text messages before joining Membership.
                  </p>
                )}
              </div>
            )}
          </Field>
        )}

        <Field label="Your name" optional={!hostLocked && !rules.require_phone && !rules.require_email}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aman" data-testid="retail-welcome-name"
            className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"/>
        </Field>

        {hostLocked ? (
          <div className="mb-4">
            <HostDestinationCard context={hostDestination} testId="retail-welcome-host-destination" />
          </div>
        ) : (
        <Field label="Order type">
          <div className="grid grid-cols-2 gap-3">
            {enabledList.map((m) => (
              <button key={m} type="button" onClick={() => setOrderType(m)} data-testid={`retail-welcome-type-${m}`}
                className={"h-14 rounded-lg text-sm font-semibold border transition flex flex-col items-center justify-center px-2 " +
                  (orderType === m ? "bg-orange-500 text-white border-orange-500" : "bg-white text-stone-700 border-stone-300 hover:border-stone-400")}>
                <span>{ORDER_MODE_FALLBACKS[m]?.icon || ""} {labelFor ? labelFor(m) : m}</span>
                <span className={"text-[10px] mt-0.5 font-normal " + (orderType === m ? "text-white/80" : "text-stone-400")}>
                  {ORDER_MODE_FALLBACKS[m]?.desc || ""}
                </span>
              </button>
            ))}
          </div>
        </Field>
        )}

        {orderType === "dine_in" && (
          <Field label="Table or order number" optional>
            <input value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder="e.g. Table 4" data-testid="retail-welcome-table"
              className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"/>
          </Field>
        )}
        {/* Go-Live Enhancement (Feb 2026, Dine-In / Takeout optional
            contact capture) — Email lives BELOW Table so the Dine-In
            field order matches the approved layout: Phone → SMS
            consent → Name → Order Type → Table (dine-in) → Email. */}
        {showEmail && (
          <Field label="Email" optional={!rules.require_email}>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. you@example.com" inputMode="email" data-testid="retail-welcome-email"
              className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"/>
            {rules.require_email && (
              <div className="mt-1.5 text-[11px] text-stone-500" data-testid="retail-welcome-email-hint">
                We&apos;ll send a 6-digit code to verify this email at checkout.
              </div>
            )}
          </Field>
        )}
        {(orderType === "delivery" || orderType === "shipping") && !hostLocked && (
          <>
            {/* Phase 2 (Customer Identity, Address & Storefront Modal
                Reliability) — the recognized-customer saved-address
                picker below was Local-Delivery-only; Parcel Shipping now
                reuses the exact same UI/logic rather than a parallel
                identity system. */}
            {/* Sprint B UX correction (Feb 2026) — phone-recognized
                returning customer flow. Auto-filled saved address gets
                a "Using saved address" label + "Deliver to a different
                address" toggle. Picking "different" clears the form
                and asks whether to save the new entry. */}
            {addressMode === "saved" && deliveryAddress.address_id && (
              <div
                className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-xs flex items-center justify-between"
                data-testid="retail-using-saved-address"
              >
                <span className="text-emerald-900 font-medium">
                  Using saved address
                </span>
                <button
                  type="button"
                  data-testid="retail-deliver-to-different"
                  onClick={() => {
                    setAddressMode("new");
                    setDeliveryAddress({
                      address_id: "", line1: "", line2: "", city: "",
                      state: "", postal_code: "", country: "US",
                      instructions: "",
                    });
                    setSavePref("none");
                    logCustomerAuditEvent(slug, { event: "new_address_entered", orderType, catalogId: catalog?.id, sessionId, customerId: recognized?.customer?.id });
                  }}
                  className="text-emerald-700 underline hover:text-emerald-900"
                >
                  Deliver to a different address
                </button>
              </div>
            )}
            {recognized?.customer?.addresses?.length > 1 && addressMode === "saved" && (
              <div className="mb-4" data-testid="retail-saved-addresses">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">
                  Saved addresses
                </div>
                <div className="space-y-2">
                  {recognized.customer.addresses.map((a) => {
                    const id = a.address_id;
                    const isPicked = (deliveryAddress.address_id === id);
                    return (
                      <button
                        type="button"
                        key={id}
                        data-testid={`retail-saved-address-${id}`}
                        onClick={() => {
                          setDeliveryAddress({
                            address_id: a.address_id,
                            line1: a.street || "", line2: a.line2 || "",
                            city: a.city || "", state: a.state || "",
                            postal_code: a.postal_code || "", country: a.country || "US",
                            instructions: a.instructions || "",
                          });
                          logCustomerAuditEvent(slug, { event: "saved_address_selected", orderType, catalogId: catalog?.id, sessionId, customerId: recognized?.customer?.id });
                        }}
                        className={
                          "w-full text-left rounded-lg border-2 p-3 transition " +
                          (isPicked
                            ? "border-orange-500 bg-orange-50"
                            : "border-stone-200 hover:border-stone-400 bg-white")
                        }
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold">
                            {a.label || (a.is_default ? "Default" : a.city)}
                          </span>
                          {a.is_default && (
                            <span className="text-[10px] uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Default</span>
                          )}
                        </div>
                        <div className="text-xs text-stone-600 mt-0.5">
                          {a.street}{a.line2 ? `, ${a.line2}` : ""} · {a.city}, {a.state} {a.postal_code}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {addressMode === "new" && recognized?.customer && (
              <div className="mb-3 rounded-lg border border-stone-300 bg-stone-50 p-3" data-testid="retail-save-address-prompt">
                <div className="text-xs font-semibold text-stone-900 mb-1.5">
                  Save this address for future orders?
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {[
                    { v: "default",    label: "Yes, save as my default address" },
                    { v: "additional", label: "Yes, save as another address" },
                    { v: "none",       label: "No, use only for this order" },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      data-testid={`retail-save-pref-${opt.v}`}
                      className={
                        "flex items-center gap-2 text-xs px-2 py-1.5 rounded cursor-pointer transition " +
                        (savePref === opt.v ? "bg-orange-50 border border-orange-300" : "bg-white border border-stone-200 hover:border-stone-400")
                      }
                    >
                      <input
                        type="radio"
                        name="save_address_pref"
                        value={opt.v}
                        checked={savePref === opt.v}
                        onChange={() => setSavePref(opt.v)}
                        className="h-3.5 w-3.5 accent-orange-500"
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {/* Iter45 / Step 2 — structured delivery address.
                Replaces the legacy free-text textarea. Layer 1 (backend)
                does field-level validation; we surface inline errors per
                field returned by the server (`fieldErrors`). */}
            <Field label="Address line 1">
              <input
                value={deliveryAddress.line1} onChange={updateAddr("line1")}
                data-testid="retail-welcome-address-line1"
                placeholder="123 Main St"
                className={"w-full h-11 rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 " +
                  (fieldErrors.line1 ? "border-rose-400" : "border-stone-300")}/>
              {fieldErrors.line1 && <div className="text-xs text-rose-600 mt-1" data-testid="retail-welcome-address-line1-error">{fieldErrors.line1}</div>}
            </Field>
            <Field label="Address line 2" optional>
              <input
                value={deliveryAddress.line2} onChange={updateAddr("line2")}
                data-testid="retail-welcome-address-line2"
                placeholder="Apt, suite, unit"
                className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City">
                <input
                  value={deliveryAddress.city} onChange={updateAddr("city")}
                  data-testid="retail-welcome-address-city"
                  className={"w-full h-11 rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 " +
                    (fieldErrors.city ? "border-rose-400" : "border-stone-300")}/>
                {fieldErrors.city && <div className="text-xs text-rose-600 mt-1">{fieldErrors.city}</div>}
              </Field>
              <Field label="State / Region">
                <input
                  value={deliveryAddress.state} onChange={updateAddr("state")}
                  data-testid="retail-welcome-address-state"
                  placeholder="CA"
                  className={"w-full h-11 rounded-lg border px-3 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-orange-300 " +
                    (fieldErrors.state ? "border-rose-400" : "border-stone-300")}/>
                {fieldErrors.state && <div className="text-xs text-rose-600 mt-1">{fieldErrors.state}</div>}
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="ZIP / Postal Code">
                <input
                  value={deliveryAddress.postal_code} onChange={updateAddr("postal_code")}
                  data-testid="retail-welcome-address-postal"
                  placeholder="94110"
                  className={"w-full h-11 rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 " +
                    (fieldErrors.postal_code ? "border-rose-400" : "border-stone-300")}/>
                {fieldErrors.postal_code && <div className="text-xs text-rose-600 mt-1">{fieldErrors.postal_code}</div>}
              </Field>
              <Field label="Country">
                <input
                  value={deliveryAddress.country} onChange={updateAddr("country")}
                  data-testid="retail-welcome-address-country"
                  maxLength={2}
                  placeholder="US"
                  className={"w-full h-11 rounded-lg border px-3 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-orange-300 " +
                    (fieldErrors.country ? "border-rose-400" : "border-stone-300")}/>
                {fieldErrors.country && <div className="text-xs text-rose-600 mt-1">{fieldErrors.country}</div>}
              </Field>
            </div>
            {orderType === "delivery" && (
              <Field label="Delivery instructions" optional>
                <textarea
                  value={deliveryAddress.instructions} onChange={updateAddr("instructions")}
                  data-testid="retail-welcome-address-instructions"
                  rows={2}
                  placeholder="Gate code, leave at door, etc."
                  className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"/>
              </Field>
            )}
            {/* Phase 4 — validation state for Parcel Shipping only. Local
                Delivery is unaffected (no carrier to validate against). */}
            {orderType === "shipping" && shippingValidation.status === "error" && (
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 mb-3"
                role="alert" data-testid="retail-welcome-shipping-validation-error"
              >
                {shippingValidation.message}
              </div>
            )}
            {orderType === "shipping" && shippingValidation.status === "needs_confirmation" && (
              <div
                className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900 space-y-2 mb-3"
                data-testid="retail-welcome-shipping-suggestion"
              >
                <div className="font-medium">We found a suggested address:</div>
                <div data-testid="retail-welcome-shipping-suggestion-text">
                  {[shippingValidation.suggestion?.street1, shippingValidation.suggestion?.street2, shippingValidation.suggestion?.city, shippingValidation.suggestion?.region, shippingValidation.suggestion?.postal].filter(Boolean).join(", ")}
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="button" data-testid="retail-welcome-shipping-suggestion-accept" className="px-3 py-1.5 rounded-md bg-sky-600 text-white text-xs font-medium" onClick={acceptShippingSuggestion}>
                    Use suggested address
                  </button>
                  <button type="button" data-testid="retail-welcome-shipping-suggestion-keep" className="px-3 py-1.5 rounded-md border border-sky-300 text-xs font-medium" onClick={keepShippingAddressAsEntered}>
                    Keep as entered
                  </button>
                </div>
              </div>
            )}
            {/* Phase 3A.5 Final Checkout Integration correction — Local
                Delivery validation states. Only rendered when the tenant
                has opted in; every other tenant's delivery flow is
                pixel-identical to before this milestone. */}
            {orderType === "delivery" && localDeliveryAddressValidationEnabled && localDeliveryValidation.status === "invalid" && (
              <div
                className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 mb-3"
                role="alert" data-testid="retail-welcome-local-delivery-invalid"
              >
                {localDeliveryValidation.message || "We couldn't confirm this address is deliverable."}
              </div>
            )}
            {orderType === "delivery" && localDeliveryAddressValidationEnabled && localDeliveryValidation.status === "error" && (
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 mb-3"
                role="alert" data-testid="retail-welcome-local-delivery-error"
              >
                {localDeliveryValidation.message || "We couldn't validate that address right now. Please try again."}
              </div>
            )}
            {orderType === "delivery" && localDeliveryAddressValidationEnabled
              && (localDeliveryValidation.status === "needs_confirmation" || localDeliveryValidation.status === "confirming") && (
              <div
                className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900 space-y-2 mb-3"
                data-testid="retail-welcome-local-delivery-confirm"
              >
                <div className="font-medium">Please confirm your delivery address:</div>
                <div data-testid="retail-welcome-local-delivery-normalized">
                  {[
                    localDeliveryValidation.normalizedAddress?.line1,
                    localDeliveryValidation.normalizedAddress?.line2,
                    localDeliveryValidation.normalizedAddress?.city,
                    localDeliveryValidation.normalizedAddress?.state,
                    localDeliveryValidation.normalizedAddress?.postal_code,
                  ].filter(Boolean).join(", ")}
                </div>
                {/* Coordinates are read-only, server-generated, and only
                    ever used to build a map link — never rendered as an
                    editable field (Phase 3A.5 §11). */}
                {localDeliveryValidation.latitude != null && localDeliveryValidation.longitude != null && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${localDeliveryValidation.latitude},${localDeliveryValidation.longitude}`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-block text-xs underline text-sky-700"
                    data-testid="retail-welcome-local-delivery-map-link"
                  >
                    View on map
                  </a>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    data-testid="retail-welcome-local-delivery-confirm-btn"
                    disabled={localDeliveryValidation.status === "confirming"}
                    className="px-3 py-1.5 rounded-md bg-sky-600 text-white text-xs font-medium disabled:opacity-50"
                    onClick={confirmLocalDeliveryAddressAndFinalize}
                  >
                    {localDeliveryValidation.status === "confirming" ? "Confirming…" : "Confirm this address"}
                  </button>
                  <button
                    type="button"
                    data-testid="retail-welcome-local-delivery-edit-btn"
                    disabled={localDeliveryValidation.status === "confirming"}
                    className="px-3 py-1.5 rounded-md border border-sky-300 text-xs font-medium disabled:opacity-50"
                    onClick={() => setLocalDeliveryValidation(LOCAL_DELIVERY_VALIDATION_IDLE)}
                  >
                    Edit address
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {error && (
          <div data-testid="retail-welcome-error" className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 mb-3">
            {error}
          </div>
        )}

        {/* Phase 3A.5 Final Checkout Integration correction — while a
            Local Delivery validation is in flight or awaiting the
            customer's explicit confirm/edit decision, the submit button
            is disabled; those states can only be resolved via the panel
            above, never by re-submitting the form. */}
        {(() => {
          const localDeliveryBlocked = orderType === "delivery" && localDeliveryAddressValidationEnabled && !hostLocked
            && ["checking", "needs_confirmation", "invalid", "confirming"].includes(localDeliveryValidation.status);
          const shippingChecking = orderType === "shipping" && shippingValidation.status === "checking";
          const blocked = shippingChecking || localDeliveryBlocked;
          let label = "Start Sales Order →";
          if (shippingChecking) label = "Verifying address…";
          else if (orderType === "delivery" && localDeliveryValidation.status === "checking") label = "Validating address…";
          else if (localDeliveryBlocked) label = "Resolve address above to continue";
          return (
            <button type="submit" data-testid="retail-welcome-submit"
              disabled={blocked}
              className={
                "mt-3 w-full h-12 rounded-lg text-white font-semibold uppercase tracking-wider transition "
                + (blocked
                  ? "bg-orange-500/40 cursor-not-allowed"
                  : "bg-orange-500 hover:bg-orange-600 active:scale-[0.99]")
              }>
              {label}
            </button>
          );
        })()}
        {/* Defect Cleanup Phase 1 (Feb 2026) — explicit Back affordance.
            Backdrop click already cancels, but kiosk + mobile customers
            need a visible CTA to return to the landing screen without
            committing to the form. */}
        <button
          type="button"
          data-testid="retail-welcome-back"
          onClick={onCancel}
          className="mt-2 w-full h-10 rounded-lg bg-transparent text-stone-500 text-sm font-medium hover:text-stone-700 hover:bg-stone-50 transition"
        >
          ← Back
        </button>
      </form>
    </StorefrontModal>
    </>
  );
}
